import React, { Component } from 'react'
import dayjs from 'dayjs'
import { observable } from 'mobx'
import { observer } from 'mobx-react'
import { Card } from '@winnin/winnin_ui.components.card'
import { Loading } from '@winnin/winnin_ui.components.loading'
import { Title } from '@winnin/winnin_ui.components.title'

import ComingSoonMessage from 'components/ComingSoonMessage'
import Error from 'components/Error'
import LineChart from 'components/LineChart'
import NoDataAvailable from 'components/NoDataAvailable'
import Switcher from 'components/Switcher'
import WarningMessage from 'components/WarningMessage'
import { client } from 'lib'
import { useTranslate } from 'lib/tolgee'
import { Filters, Chart, TOLGEE_GLOBALS } from 'utils'

import { ReactComponent as EmptyState } from '../../assets/images/svg/empty-videos-about-your-brand.svg'
import EmptyStateNoData from '../EmptyStateNoData'

import './index.scss'

const METRICS = ['views', 'likes']

@observer
class ChartCardMonoMetric extends Component {
  @observable
  loading = true

  @observable
  error = false

  @observable
  performance = []

  @observable
  chartLogic = {
    selected: Filters.get('chart', 'chartLogic', 'cumulative'),
    options: Filters.get('chart', 'chartLogic'),
    daily: this.props.daily,
  }

  @observable
  shouldRenderThirtyDay = true

  @observable
  isMonthlyGraphEmpty = true

  componentDidMount() {
    this.loadData()
  }

  componentDidUpdate(prevProps) {
    const currentVars = JSON.stringify(this.props.variables)
    const prevVars = JSON.stringify(prevProps.variables)

    currentVars !== prevVars && this.loadData()
  }

  buildOptions(categories, options) {
    return {
      chart: {
        height: 200,
        zoom: {
          type: 'x',
          enabled: true,
        },
        animations: {
          enabled: false,
        },
      },
      xaxis: {
        categories,
      },
      yaxis: {},
      ...options,
    }
  }

  handleChange = (field, opts) => {
    if (this[field]) {
      this[field].selected = opts
    }
  }

  changeChartLogic = opts => {
    this.handleChange('chartLogic', opts)
    this.loadData()
  }

  variables() {
    const { chartLogic } = this
    const { cumulative } = this.props
    const defaultVars = { dateRange: 12 }
    if (cumulative && chartLogic.selected.value === 'cumulative') {
      return { ...defaultVars, cumulative: true }
    }

    return defaultVars
  }

  loadData = async () => {
    if (Object.keys(this.props.variables).length === 0) return

    const { query, performanceExtractor } = this.props
    const variables = { ...this.props.variables, ...this.variables() }

    try {
      this.loading = true
      const response = await client.query({ query, variables })
      this.performance = performanceExtractor(response.data)
      this.shouldRenderThirtyDay = await this.isThirtyDayGraphValid(this.performance)
      this.isMonthlyGraphEmpty = await this.checkEmptyMonthlyGraph(variables)
    } catch (e) {
      this.error = true
      console.error(e)
    } finally {
      this.loading = false
    }
  }

  isThirtyDayGraphValid = performance => {
    const { publishedAt, notValidCallback = () => {} } = this.props
    const dateLimit = dayjs().subtract(1, 'month').format()
    const isOld = dayjs(publishedAt).isBefore(dateLimit)
    const isEmpty = METRICS.every(key => performance.every(element => element?.[key] === null))

    const isValid = !(isEmpty && isOld)

    !isValid && notValidCallback(false)

    return isValid
  }

  checkEmptyMonthlyGraph = async variables => {
    //variables empty
    if (Object.keys(this.props.variables).length === 0) return

    const { query, performanceExtractor } = this.props

    try {
      const responseCumulative = await client.query({ query, variables, cumulative: true })
      const performanceCumulative = performanceExtractor(responseCumulative.data)
      const isEmptyCumulative = METRICS.every(key => performanceCumulative.every(element => element?.[key] === null))

      const responseMonthly = await client.query({ query, variables: { ...variables, cumulative: false } })
      const performanceMonthly = performanceExtractor(responseMonthly.data)
      const isEmptyMonthly = METRICS.every(key => performanceMonthly.every(element => element?.[key] === null))

      return isEmptyCumulative || isEmptyMonthly
    } catch (e) {
      console.warn(e)
    }
  }

  renderChart() {
    const { options, comingSoonCondition = () => false, comingSoonMessage = '' } = this.props
    const { daily } = this.chartLogic
    const { loading, performance } = this

    // we can alter this function and pass whatever we need for custom conditions of coming soon messages in the future
    // Right now, I just need the sortByValue:
    const showComingSoonMessage = comingSoonCondition({ sortByValue: this.props.sortBy })

    if (!loading && performance) {
      const { sortBy, sortByLabel } = this.props

      const formattedPerformance = performance?.reduce((acc, values) => {
        if (values?.[sortByLabel?.toLowerCase()] !== null) {
          return [...acc, values]
        }
        return acc
      }, [])

      const performanceSeries = Chart.buildPerformanceSeries({ sortBy, sortByLabel, performance })
      const formattedSeries = [
        {
          data: performanceSeries?.[0]?.data?.filter(item => item !== null),
          name: performanceSeries?.[0]?.name,
        },
      ]
      const performanceCategories = Chart.buildPerformanceCategories(formattedPerformance, daily ? 'DD/MM/YYYY' : null)
      const categories = performanceCategories
      const parsedCategories = categories?.length
        ? categories.map(category => {
            return Object.hasOwn(category || {}, 'date') ? category.date : category
          })
        : []

      const isEmpty = Chart.checkEmptyGraph(formattedSeries)
      const hasRemoved = daily ? Chart.removeEmptyValues(formattedSeries, parsedCategories) : null
      const chartOptions = this.buildOptions(parsedCategories, options)
      const notEnoughData = formattedSeries
        ? formattedSeries?.length && formattedSeries?.length === 1 && formattedSeries?.[0]?.data?.every(el => el === 0)
        : true

      if (isEmpty || this.isMonthlyGraphEmpty) {
        return <ComingSoon />
      }
      if (notEnoughData) return <NoDataAvailable />
      if (showComingSoonMessage) return <ComingSoonMessage message={comingSoonMessage} />

      return (
        <div className="chart-card-content">
          <LineChart options={chartOptions} series={formattedSeries} width={this.props.chartWidth} />
          {this.renderNegativeGraphMessage(formattedSeries)}
          {this.renderDecreasingGraphMessage(formattedSeries)}
          {this.renderEmptyGraphMessage(formattedSeries)}
          {hasRemoved && <p>* Data collection began a few days after the video was uploaded.</p>}
        </div>
      )
    }

    return null
  }

  renderNegativeGraphMessage(series) {
    const { chartLogic } = this
    const message =
      'Negative values may occur when video settings, such as hiding comments or likes, are changed by the owner.'
    const haveNegativeValue = Chart.checkNegativeGraph(series)

    if (haveNegativeValue && chartLogic.selected.value === 'monthly') {
      return <WarningMessage message={message} />
    }
  }

  renderDecreasingGraphMessage(series) {
    const { chartLogic } = this
    const message =
      'Decreasing values may occur when video settings, such as hiding comments or likes, are changed by the owner.'
    const haveDecreasingValue = Chart.checkDecreasingGraph(series)

    if (haveDecreasingValue && chartLogic.selected.value === 'cumulative') {
      return <WarningMessage message={message} />
    }
  }

  renderEmptyGraphMessage(series) {
    if (!this.props.daily) {
      const message = 'There’s no data available yet.'
      const isEmpty = Chart.checkEmptyGraph(series)

      if (isEmpty) return <WarningMessage message={message} />
    }
  }

  renderContent() {
    const { title, cumulative, notHasViews, emptyStateText } = this.props

    const { chartLogic, loading, error } = this

    if (error) {
      return <Error errorMessage={'Error loading chart.'} buttonText={'Retry'} handleError={this.loadData} />
    }

    if (notHasViews) {
      return (
        <div className="title-content">
          <div className="creator-subtitle">
            <Title tag="h3" className="title">
              {title}
            </Title>
          </div>
          <EmptyStateViewsNotCollected />
        </div>
      )
    }

    if (emptyStateText) {
      return (
        <div className="title-content">
          <div className="creator-subtitle">
            <Title tag="h3" className="title">
              {title}
            </Title>
          </div>
          <EmptyStateNoData image={<EmptyState />} text={emptyStateText} />
        </div>
      )
    }

    return (
      <div className="chart-card title-actions-content">
        <Title tag="h3" className="title">
          {title}
        </Title>
        <div className="grid-of-control actions">
          {cumulative && (
            <Switcher
              selected={chartLogic.selected}
              options={chartLogic.options}
              onChange={this.changeChartLogic}
              daily={chartLogic.daily}
            />
          )}
        </div>
        <Card className="content">{loading ? <Loading /> : this.renderChart()}</Card>
      </div>
    )
  }

  render() {
    return this.shouldRenderThirtyDay ? this.renderContent() : null
  }
}

function ComingSoon() {
  const t = useTranslate()

  const emptyDataTitle = t(TOLGEE_GLOBALS.emptyDataTitle)
  const emptyDataMessage = t(TOLGEE_GLOBALS.emptyDataMessage)

  return (
    <NoDataAvailable className="coming-soon" icon="coming-soon" title={emptyDataTitle} message={emptyDataMessage} />
  )
}

function EmptyStateViewsNotCollected() {
  const t = useTranslate()
  const text = t(TOLGEE_GLOBALS.platformViewsNotCollected)

  return <EmptyStateNoData image={<EmptyState />} text={text} />
}

ChartCardMonoMetric.propTypes = {}

ChartCardMonoMetric.defaultProps = {
  cumulative: false,
}

ChartCardMonoMetric.displayName = 'ChartCardMonoMetric'

export { ChartCardMonoMetric }
